/**
 * Slide Capture Utility
 * 
 * Extracts slide data (image, PPTX, metadata) from Office.js
 * and manages synchronization with IndexedDB storage.
 * 
 * Used by:
 * - LiveTracker (background change detection)
 * - ReadSlideTool (on-demand capture before reading)
 */

import { LiveTrackingStorage, SlideTrackingSnapshot, QuickMetadata } from '../tracking/liveTrackingStorage';
import { getMasterInfoInContext, SlideMasterInfo } from '../slideMasterExtractor';
import { slideId as normalizeSlideId, layoutId as normalizeLayoutId, masterId as normalizeMasterId } from '../../utils/idNormalizer';
import { backendClient } from '../communication/backendClient';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Raw captured data from Office.js (before IndexedDB sync)
 */
export interface SlideCaptureData {
  slideId: string;
  slideIndex: number;
  imageBase64: string;
  pptxBase64: string;
  layoutId?: string;
  layoutName?: string;
  masterTemplateId?: string;
  masterTemplateName?: string;
}

/**
 * Result of capture and sync operation
 */
export interface CaptureResult {
  snapshot: SlideTrackingSnapshot;
  hasChanged: boolean;
  isNew: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// SLIDE CAPTURE CLASS
// ═══════════════════════════════════════════════════════════════════════════

export class SlideCapture {
  private storage: LiveTrackingStorage;

  constructor(storage?: LiveTrackingStorage) {
    this.storage = storage || new LiveTrackingStorage();
  }

  /**
   * Initialize storage (must be called before using)
   */
  async initialize(): Promise<void> {
    await this.storage.initialize();
  }

  /**
   * Capture slide data from Office.js within an existing context
   * Does NOT touch IndexedDB - just captures raw data
   */
  async captureSlideData(
    context: PowerPoint.RequestContext,
    slide: PowerPoint.Slide,
    slideIndex: number
  ): Promise<SlideCaptureData> {
    // Get slide ID
    slide.load('id');
    await context.sync();
    const slideIdNormalized = normalizeSlideId(slide.id);

    // Capture image
    const imageResult = slide.getImageAsBase64();
    await context.sync();
    const imageBase64 = imageResult.value || '';
    console.log(`[SlideCapture] Captured image for slide ${slideIndex + 1}: ${Math.round(imageBase64.length / 1024)}KB`);

    // Capture PPTX
    let pptxBase64 = '';
    try {
      const pptxResult = slide.exportAsBase64();
      await context.sync();
      pptxBase64 = pptxResult.value || '';
      console.log(`[SlideCapture] Captured PPTX for slide ${slideIndex + 1}: ${Math.round(pptxBase64.length / 1024)}KB`);
    } catch (e) {
      console.warn(`[SlideCapture] Could not export PPTX for slide ${slideIndex + 1}:`, e);
    }

    // Get layout info
    let layoutId: string | undefined;
    let layoutName: string | undefined;
    try {
      const layout = slide.layout;
      layout.load('id,name');
      await context.sync();
      layoutId = normalizeLayoutId(layout.id);
      layoutName = layout.name;
    } catch (e) {
      console.warn(`[SlideCapture] Could not get layout info for slide ${slideIndex + 1}:`, e);
    }

    // Get master info
    let masterTemplateId: string | undefined;
    let masterTemplateName: string | undefined;
    try {
      const masterInfo = await getMasterInfoInContext(context, slide);
      if (masterInfo) {
        masterTemplateId = masterInfo.masterId;
        masterTemplateName = masterInfo.masterName;
      }
    } catch (e) {
      console.warn(`[SlideCapture] Could not get master info for slide ${slideIndex + 1}:`, e);
    }

    return {
      slideId: slideIdNormalized,
      slideIndex,
      imageBase64,
      pptxBase64,
      layoutId,
      layoutName,
      masterTemplateId,
      masterTemplateName,
    };
  }

  /**
   * Type guard to validate QuickMetadata structure from API response
   */
  private isValidQuickMetadata(data: unknown): data is QuickMetadata {
    if (typeof data !== 'object' || data === null) {
      return false;
    }
    
    const meta = data as Record<string, unknown>;
    
    // Check required arrays exist and are arrays
    if (!Array.isArray(meta.fillColors) || !Array.isArray(meta.outlineColors) ||
        !Array.isArray(meta.textColors) || !Array.isArray(meta.styleCombos)) {
      return false;
    }
    
    // slideTitle is optional but if present must be string or null
    if (meta.slideTitle !== undefined && meta.slideTitle !== null && typeof meta.slideTitle !== 'string') {
      return false;
    }

    // schemaVersion is optional but if present must be a finite number
    if (meta.schemaVersion !== undefined) {
      if (typeof meta.schemaVersion !== 'number' || !Number.isFinite(meta.schemaVersion)) {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Extract quick metadata from pptxBase64 by calling parse endpoint.
   * Uses V2 endpoint only (Aspose-based).
   * Requires "complete" detail level to get fill/outline/font data.
   * 
   * @param pptxBase64 - Exported PPTX data
   * @returns Quick metadata or undefined if extraction fails
   */
  async extractQuickMetadata(pptxBase64: string): Promise<QuickMetadata | undefined> {
    try {
      const quickMetadata = await backendClient.parseSlideQuickMetadata(pptxBase64);
      
      // Validate structure before returning
      if (quickMetadata && this.isValidQuickMetadata(quickMetadata)) {
        console.log(`[SlideCapture] ✅ V2 metadata: title="${quickMetadata.slideTitle || '(no title)'}"`);
        return quickMetadata;
      } else if (quickMetadata) {
        console.warn(`[SlideCapture] ⚠️ V2 malformed quick metadata:`, quickMetadata);
        return undefined;
      }
      
      return undefined;
    } catch (error) {
      console.warn(`[SlideCapture] Error extracting quick metadata:`, error);
      return undefined;
    }
  }

  /**
   * Compare captured data with stored snapshot
   * Returns whether the slide has changed
   */
  async compareWithStored(
    documentId: string,
    capturedData: SlideCaptureData
  ): Promise<{ hasChanged: boolean; isNew: boolean; storedSnapshot?: SlideTrackingSnapshot }> {
    const storedSnapshot = await this.storage.getSnapshot(documentId, capturedData.slideId);

    if (!storedSnapshot) {
      console.log(`[SlideCapture] No stored snapshot for slide ${capturedData.slideId} - treating as new`);
      return { hasChanged: true, isNew: true };
    }

    // Direct 1-to-1 base64 string comparison
    const hasChanged = capturedData.imageBase64 !== storedSnapshot.imageBase64;
    
    if (hasChanged) {
      console.log(`[SlideCapture] Change detected in slide ${capturedData.slideIndex + 1}`);
      if (capturedData.imageBase64.length !== storedSnapshot.imageBase64.length) {
        console.log(`[SlideCapture] Image size changed: ${storedSnapshot.imageBase64.length} → ${capturedData.imageBase64.length}`);
      }
    } else {
      console.log(`[SlideCapture] No changes in slide ${capturedData.slideIndex + 1}`);
    }

    return { hasChanged, isNew: false, storedSnapshot };
  }

  /**
   * Capture, compare, and sync to IndexedDB
   * This is the main entry point for most use cases
   * 
   * Returns the updated snapshot and whether it changed
   */
  async captureAndSync(
    context: PowerPoint.RequestContext,
    slide: PowerPoint.Slide,
    slideIndex: number,
    documentId: string
  ): Promise<CaptureResult> {
    // 1. Capture current state from Office.js
    const capturedData = await this.captureSlideData(context, slide, slideIndex);

    // 2. Compare with stored snapshot
    const { hasChanged, isNew, storedSnapshot } = await this.compareWithStored(documentId, capturedData);

    // 3. Extract quick metadata if slide changed or is new
    let quickMetadata = storedSnapshot?.quickMetadata;  // Preserve existing by default
    
    if (capturedData.pptxBase64 && (hasChanged || isNew)) {
      try {
        quickMetadata = await this.extractQuickMetadata(capturedData.pptxBase64);
      } catch (error) {
        console.warn(`[SlideCapture] Could not extract quick metadata:`, error);
        // Keep old metadata if extraction fails
      }
    }

    // 4. Build snapshot object WITH quick metadata
    const shouldUpdatePrev = hasChanged && !!storedSnapshot;
    const prevImageBase64 = shouldUpdatePrev ? storedSnapshot?.imageBase64 : storedSnapshot?.prevImageBase64;
    const prevPptxBase64 = shouldUpdatePrev ? storedSnapshot?.pptxBase64 : storedSnapshot?.prevPptxBase64;
    const prevTimestamp = shouldUpdatePrev ? storedSnapshot?.timestamp : storedSnapshot?.prevTimestamp;

    const snapshot: SlideTrackingSnapshot = {
      documentId: documentId,
      slideId: capturedData.slideId,
      slideIndex: capturedData.slideIndex,
      imageBase64: capturedData.imageBase64,
      pptxBase64: capturedData.pptxBase64,
      prevImageBase64,
      prevPptxBase64,
      prevTimestamp,
      layoutId: capturedData.layoutId,
      layoutName: capturedData.layoutName,
      masterTemplateId: capturedData.masterTemplateId,
      masterTemplateName: capturedData.masterTemplateName,
      timestamp: Date.now(),
      lastProcessed: storedSnapshot?.lastProcessed,
      touchCount: (storedSnapshot?.touchCount || 0) + (hasChanged ? 1 : 0),
      shortDescription: storedSnapshot?.shortDescription,
      descriptionUpdatedAt: storedSnapshot?.descriptionUpdatedAt,
      quickMetadata: quickMetadata,
    };

    // 5. Save to IndexedDB (always, to keep timestamp fresh)
    if (hasChanged || isNew) {
      await this.storage.saveSnapshot(snapshot);
      console.log(`[SlideCapture] ${isNew ? 'Created' : 'Updated'} snapshot for slide ${capturedData.slideIndex + 1}`);
    }

    return {
      snapshot,
      hasChanged,
      isNew,
    };
  }

  /**
   * Extract quick metadata for multiple slides in parallel using batch endpoint.
   * 
   * Uses V2 endpoint only (/slides/parse_batch_v2).
   * This method sends all slides in a single HTTP request to the backend,
   * which processes them in parallel (up to 10 concurrent) for efficiency.
   * 
   * @param slidesData - Array of {slideId, pptxBase64} objects to extract metadata from
   * @returns Promise resolving to Map of slideId -> QuickMetadata. Only successful extractions
   *          are included in the Map. Failed slides are logged but not returned. Empty Map
   *          indicates complete failure (network error, etc.)
   * 
   * @example
   * ```typescript
   * const slides = [
   *   { slideId: 'slide_318', pptxBase64: '...' },
   *   { slideId: 'slide_319', pptxBase64: '...' }
   * ];
   * const metadataMap = await slideCapture.extractQuickMetadataBatch(slides);
   * // metadataMap.get('slide_318') -> { slideTitle: 'Revenue', fillColors: [...], ... }
   * ```
   * 
   * @throws Does not throw - returns empty Map on network/server errors
   */
  async extractQuickMetadataBatch(
    slidesData: Array<{ slideId: string; pptxBase64: string }>
  ): Promise<Map<string, QuickMetadata>> {
    console.log(`[SlideCapture] Batch extracting metadata for ${slidesData.length} slides...`);

    // V2 endpoint only
    try {
      const v2Data = await backendClient.parseSlideQuickMetadataBatch(slidesData) as
        | {
            successful?: number;
            total_requested?: number;
            results: Array<{
              slide_id: string;
              success: boolean;
              quick_metadata?: QuickMetadata;
            }>;
          }
        | undefined;

      if (v2Data) {
        console.log(
          `[SlideCapture] ✅ V2 batch extracted: ${v2Data.successful ?? 0}/${v2Data.total_requested ?? slidesData.length} successful`
        );
        return this.parseBatchResults(v2Data);
      }
      console.warn(`[SlideCapture] V2 batch parse failed`);
      return new Map();
    } catch (error) {
      console.warn(`[SlideCapture] Error in batch metadata extraction:`, error);
      return new Map();
    }
  }
  
  /**
   * Parse batch results from V2 endpoint.
   */
  private parseBatchResults(data: {
    results: Array<{
      slide_id: string;
      success: boolean;
      quick_metadata?: QuickMetadata;
    }>;
  }): Map<string, QuickMetadata> {
    const metadataMap = new Map<string, QuickMetadata>();
    let validCount = 0;
    let invalidCount = 0;
    
    for (const result of data.results) {
      if (result.success && result.quick_metadata) {
        if (this.isValidQuickMetadata(result.quick_metadata)) {
          metadataMap.set(result.slide_id, result.quick_metadata);
          validCount++;
        } else {
          console.warn(`[SlideCapture] ⚠️ Invalid metadata structure for ${result.slide_id}`);
          invalidCount++;
        }
      }
    }
    
    if (invalidCount > 0) {
      console.warn(`[SlideCapture] ⚠️ ${invalidCount} slides had invalid metadata structure`);
    }
    
    return metadataMap;
  }

  /**
   * Capture and sync multiple slides
   * Returns array of results with change flags
   */
  async captureAndSyncMultiple(
    context: PowerPoint.RequestContext,
    slides: PowerPoint.Slide[],
    documentId: string
  ): Promise<CaptureResult[]> {
    const results: CaptureResult[] = [];

    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      const result = await this.captureAndSync(context, slide, i, documentId);
      results.push(result);
    }

    return results;
  }

  /**
   * Get storage instance for direct access if needed
   */
  getStorage(): LiveTrackingStorage {
    return this.storage;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// SINGLETON INSTANCE
// ═══════════════════════════════════════════════════════════════════════════

// Default shared instance (can be replaced if needed)
let defaultInstance: SlideCapture | null = null;

export function getSlideCapture(): SlideCapture {
  if (!defaultInstance) {
    defaultInstance = new SlideCapture();
  }
  return defaultInstance;
}

export function setSlideCapture(instance: SlideCapture): void {
  defaultInstance = instance;
}

